import { useState } from 'react';
import {
  BODY_TYPES_WITH_CRANE,
  OTHER_MODEL,
  TOWED_KINDS,
  TRAILER_MAKES,
  TRUCK_BRANDS,
  VEHICLE_COLORS,
  VEHICLE_KINDS,
  VEHICLE_KIND_LABEL,
  modelsOf,
  vehicleYears,
  type BodyType,
  type VehicleKind,
} from 'shared';
import { api } from '../api';
import BodyTypeSelect from '../BodyTypeSelect';
import FieldError from '../FieldError';
import { useFormErrors } from '../useFormErrors';
import { toast } from '../toast';

interface Props {
  companyId: string;
  onDone: () => void;
}

/**
 * Заведение техники в парк компании.
 *
 * Раньше машину заводили только в профиле — то есть на себя. Для конторы это
 * неверно по сути: техника принадлежит организации, водитель уходит, машина
 * остаётся. Здесь она сразу записывается на компанию, поэтому форма и живёт
 * в парке, а не в личных настройках.
 */
export default function FleetAdd({ companyId, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [kind, setKind] = useState<VehicleKind>('TRUCK');
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');
  const [bodyType, setBodyType] = useState<BodyType>('TENT');
  const [capacityT, setCapacityT] = useState('');
  const [volumeM3, setVolumeM3] = useState('');
  const [adr, setAdr] = useState(false);
  const [craneT, setCraneT] = useState('');
  const [craneReach, setCraneReach] = useState('');

  const form = useFormErrors();
  const towed = TOWED_KINDS.includes(kind);

  async function submit() {
    // Проверка до сети: ждать ответа ради «укажите номер» человек не должен.
    if (plate.trim().length < 4) return form.fail({ plate: 'Укажите госномер' });
    if (!Number(capacityT)) {
      return form.fail({ capacityT: 'Укажите грузоподъёмность в тоннах' });
    }

    form.reset();
    setBusy(true);
    try {
      await api.addVehicle({
        kind,
        make: make || undefined,
        model: model && model !== OTHER_MODEL ? model : undefined,
        year: year ? Number(year) : undefined,
        color: color || undefined,
        plate: plate.trim().toUpperCase(),
        bodyType,
        capacityKg: Math.round(Number(capacityT) * 1000),
        volumeM3: volumeM3 ? Number(volumeM3) : undefined,
        adr,
        companyId,
        craneCapacityKg: craneT ? Math.round(Number(craneT) * 1000) : null,
        craneReachM: craneReach ? Number(craneReach) : null,
      });

      toast.success(`${VEHICLE_KIND_LABEL[kind]} ${plate.toUpperCase()} в парке`);
      setPlate('');
      setMake('');
      setModel('');
      setYear('');
      setColor('');
      setCapacityT('');
      setVolumeM3('');
      setCraneT('');
      setCraneReach('');
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        className="secondary btn--compact"
        style={{ marginBottom: 'var(--s3)' }}
        onClick={() => setOpen(true)}
      >
        Добавить технику
      </button>
    );
  }

  return (
    <div className="card">
      <h3>Новая единица техники</h3>

      <div className="field-row">
        <div className="field">
          <label>Что заводим</label>
          <select value={kind} onChange={(e) => setKind(e.target.value as VehicleKind)}>
            {VEHICLE_KINDS.map((k) => (
              <option key={k} value={k}>
                {VEHICLE_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Госномер</label>
          <input
            {...form.field('plate')}
            autoCapitalize="characters"
            placeholder="А123ВС777"
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
          />
          <FieldError form={form} name="plate" />
        </div>
      </div>

      {/*
        * Марка и модель — из справочника, а не свободным вводом.
        *
        * «ман», «MAN» и «Ман TGX» в парке одной конторы — три разные машины,
        * по которым нельзя ни отобрать, ни отчитаться. Заказчику на выгрузке
        * тоже нужна не строка из чужой головы, а «белый MAN, номер такой-то».
        */}
      <div className="field-row">
        <div className="field">
          <label>Марка</label>
          <select
            value={make}
            onChange={(e) => {
              setMake(e.target.value);
              // Модель принадлежит марке: при смене марки прежняя теряет смысл.
              setModel('');
            }}
          >
            <option value="">Не указана</option>
            {towed
              ? TRAILER_MAKES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))
              : TRUCK_BRANDS.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.makes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </optgroup>
                ))}
          </select>
        </div>

        <div className="field">
          <label>Модель</label>
          {modelsOf(make).length > 0 ? (
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              <option value="">Не указана</option>
              {modelsOf(make).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value={OTHER_MODEL}>{OTHER_MODEL}</option>
            </select>
          ) : (
            // Марки нет в справочнике или модель редкая — принимаем текстом:
            // техника, которой нет в списке, существует.
            <input
              placeholder="Модель"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          )}
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label>Год выпуска</label>
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Не указан</option>
            {vehicleYears().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Цвет</label>
          <select value={color} onChange={(e) => setColor(e.target.value)}>
            <option value="">Не указан</option>
            {VEHICLE_COLORS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Кузов</label>
        <BodyTypeSelect value={bodyType} onChange={(v) => v && setBodyType(v)} />
      </div>

      <div className="field-row">
        <div className="field">
          <label>Грузоподъёмность, т</label>
          <input
            {...form.field('capacityT')}
            type="number"
            inputMode="decimal"
            value={capacityT}
            onChange={(e) => setCapacityT(e.target.value)}
          />
          <FieldError form={form} name="capacityT" />
        </div>
        <div className="field">
          <label>Объём, м³</label>
          <input
            type="number"
            inputMode="decimal"
            value={volumeM3}
            onChange={(e) => setVolumeM3(e.target.value)}
          />
        </div>
      </div>

      {/* Параметры крана спрашиваем только у манипулятора: у тента они
          сбивают с толку и мешают подбору. */}
      {BODY_TYPES_WITH_CRANE.includes(bodyType) && (
        <div className="field-row">
          <div className="field">
            <label>Кран поднимает, т</label>
            <input
              type="number"
              inputMode="decimal"
              value={craneT}
              onChange={(e) => setCraneT(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Вылет стрелы, м</label>
            <input
              type="number"
              inputMode="decimal"
              value={craneReach}
              onChange={(e) => setCraneReach(e.target.value)}
            />
          </div>
        </div>
      )}

      <label className="check">
        <input type="checkbox" checked={adr} onChange={(e) => setAdr(e.target.checked)} />
        допуск ADR
      </label>

      {towed && (
        <div className="meta" style={{ marginTop: 8 }}>
          Прицеп цепляется к тягачу в его карточке — водитель ему не нужен.
        </div>
      )}

      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button disabled={busy} onClick={() => void submit()}>
          {busy ? 'Завожу…' : 'Добавить'}
        </button>
        <button className="secondary" disabled={busy} onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
    </div>
  );
}
